import { useState, useCallback } from 'react';
import { notify } from '../../../utils/notifications';
import api from '../../../api/api';
import DraggableList from '../shared/DraggableList';
import EntityPicker from '../shared/EntityPicker';
import PromptEditor from '../shared/PromptEditor';
import ConfirmDialog from '../shared/ConfirmDialog';
import '../WorkflowEditor.css';

function DragHandle({ listeners }) {
  return (
    <div className="wfe-drag-handle" {...listeners}>
      <div className="wfe-drag-dots">
        <span /><span /><span /><span /><span /><span />
      </div>
    </div>
  );
}

function FlagRow({ flag, index, categoryIndex, dispatch, allConfigs, dragListeners }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const usedByCount = allConfigs.filter((c) =>
    (c.red_flags || []).some((cat) => (cat.flag_refs || []).includes(flag?.id))
  ).length;

  const startEdit = () => {
    if (!flag) return;
    setEditName(flag.name || '');
    setEditDescription(flag.description || '');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await api.put(`/admin/red-flags/${flag.id}`, {
        name: editName,
        description: editDescription,
      });
      dispatch({ type: 'UPDATE_FLAG', flag: resp.data });
      notify.success('Flag updated');
      setEditing(false);
    } catch {
      notify.error('Failed to update flag');
    } finally {
      setSaving(false);
    }
  };

  if (!flag) {
    return (
      <div className="wfe-field-row wfe-field-missing">
        <DragHandle listeners={dragListeners} />
        <span className="wfe-field-name" style={{ color: '#DC2626' }}>Missing flag reference</span>
        <div className="wfe-field-actions-visible">
          <button
            className="wfe-field-btn danger"
            onClick={() => dispatch({ type: 'REMOVE_FLAG_FROM_CATEGORY', categoryIndex, flagIndex: index })}
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`wfe-field-row ${editing ? 'wfe-field-editing' : ''}`}>
        <DragHandle listeners={dragListeners} />
        <span className="wfe-field-name">{flag.name}</span>
        {!editing && flag.description && (
          <span className="wfe-field-preview">
            "{flag.description.slice(0, 50)}{flag.description.length > 50 ? '...' : ''}"
          </span>
        )}
        <div className={editing ? 'wfe-field-actions-visible' : 'wfe-field-actions'}>
          {editing ? (
            <button className="wfe-field-btn" style={{ color: '#0D9488', fontWeight: 500 }}>Editing</button>
          ) : (
            <button className="wfe-field-btn" onClick={startEdit}>Edit</button>
          )}
          <button
            className="wfe-field-btn danger"
            onClick={() => setConfirmRemove(true)}
          >
            Remove
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title={`Remove "${flag.name}"?`}
        message="This removes the flag from this category. The flag definition will still exist and can be re-added later."
        confirmLabel="Remove"
        onConfirm={() => { setConfirmRemove(false); dispatch({ type: 'REMOVE_FLAG_FROM_CATEGORY', categoryIndex, flagIndex: index }); }}
        onCancel={() => setConfirmRemove(false)}
      />

      {editing && (
        <div className="wfe-inline-edit">
          {usedByCount > 1 && (
            <div className="wfe-shared-warning">
              <span>&#9888;</span>
              <span>
                This flag is used by <strong>{usedByCount} facility configs</strong>. Changes will affect all of them.
              </span>
            </div>
          )}

          <div className="wfe-form-group" style={{ marginBottom: 14 }}>
            <label className="wfe-form-label">Flag Name</label>
            <input className="wfe-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <PromptEditor
              label="Description"
              value={editDescription}
              onChange={setEditDescription}
              rows={2}
              showStats={false}
              placeholder="Brief description of this red flag condition..."
            />
          </div>

          <div className="wfe-inline-edit-footer">
            <button className="wfe-btn-sm primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Flag'}
            </button>
            <button className="wfe-btn-sm ghost" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}

function CategoryCard({ category, categoryIndex, dispatch, allFlags, allConfigs, dragListeners }) {
  const [expanded, setExpanded] = useState(categoryIndex === 0);
  const [showPicker, setShowPicker] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFlag, setNewFlag] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [confirmRemoveCategory, setConfirmRemoveCategory] = useState(false);

  const handleCreateFlag = async () => {
    if (!newFlag.name.trim()) return;
    setCreating(true);
    try {
      const id = newFlag.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      const resp = await api.post('/admin/red-flags', {
        id,
        name: newFlag.name.trim(),
        description: newFlag.description.trim(),
        category: category.category_name.toLowerCase().replace(/\s+/g, '_'),
      });
      dispatch({ type: 'ADD_FLAG', flag: resp.data });
      dispatch({ type: 'ADD_FLAG_TO_CATEGORY', categoryIndex, flagId: resp.data.id });
      setNewFlag({ name: '', description: '' });
      setShowCreateForm(false);
      notify.success('Flag created and added');
    } catch (err) {
      if (err.response?.status === 409) {
        notify.error('A flag with this ID already exists');
      } else {
        notify.error('Failed to create flag');
      }
    } finally {
      setCreating(false);
    }
  };

  const getConfigFlagRefs = useCallback((config) => {
    return (config.red_flags || []).flatMap((c) => c.flag_refs || []);
  }, []);

  return (
    <div className="wfe-group">
      <div className="wfe-group-header" onClick={() => setExpanded(!expanded)}>
        <DragHandle listeners={dragListeners} />
        <div className="wfe-group-order">{category.order || categoryIndex + 1}</div>
        <span className="wfe-group-name">{category.category_name}</span>
        <span className="wfe-group-count">{(category.flag_refs || []).length} flags</span>
        <div className="wfe-group-actions">
          <span className={`wfe-chevron ${expanded ? 'open' : ''}`}>&#9662;</span>
          <button
            className="wfe-icon-btn danger"
            onClick={(e) => { e.stopPropagation(); setConfirmRemoveCategory(true); }}
            title="Remove category"
          >
            &times;
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemoveCategory}
        title={`Remove "${category.category_name}" category?`}
        message={`This will remove the category and its ${(category.flag_refs || []).length} flag references from this config. The flag definitions will still exist.`}
        confirmLabel="Remove Category"
        onConfirm={() => { setConfirmRemoveCategory(false); dispatch({ type: 'REMOVE_CATEGORY', index: categoryIndex }); }}
        onCancel={() => setConfirmRemoveCategory(false)}
      />

      {expanded && (
        <>
          <div className="wfe-group-body">
            <DraggableList
              items={category.flag_refs || []}
              keyExtractor={(id) => id}
              onReorder={(newRefs) => dispatch({
                type: 'REORDER_FIELDS_IN_GROUP',
                groupIndex: categoryIndex,
                fieldRefs: newRefs,
              })}
              renderItem={(flagId, idx, { dragListeners: dl }) => (
                <FlagRow
                  flag={allFlags.find((f) => f.id === flagId)}
                  index={idx}
                  categoryIndex={categoryIndex}
                  dispatch={dispatch}
                  allConfigs={allConfigs}
                  dragListeners={dl}
                />
              )}
            />
          </div>

          {showCreateForm && (
            <div className="wfe-inline-edit" style={{ borderTop: '1px solid #F0F0EA' }}>
              <div className="wfe-form-group" style={{ marginBottom: 14 }}>
                <label className="wfe-form-label">Flag Name</label>
                <input
                  className="wfe-input"
                  placeholder="e.g., Active TB"
                  value={newFlag.name}
                  onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <PromptEditor
                  label="Description"
                  value={newFlag.description}
                  onChange={(d) => setNewFlag({ ...newFlag, description: d })}
                  rows={2}
                  showStats={false}
                  placeholder="Brief description..."
                />
              </div>
              <div className="wfe-inline-edit-footer">
                <button className="wfe-btn-sm primary" onClick={handleCreateFlag} disabled={creating}>
                  {creating ? 'Creating...' : 'Create & Add'}
                </button>
                <button className="wfe-btn-sm ghost" onClick={() => setShowCreateForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div className="wfe-group-footer">
            <button className="wfe-btn-add-sm" onClick={() => setShowCreateForm(true)}>
              + Create New Flag
            </button>
            <button className="wfe-btn-add-sm" onClick={() => setShowPicker(true)}>
              + Add Existing Flag
            </button>
          </div>

          <EntityPicker
            open={showPicker}
            onClose={() => setShowPicker(false)}
            title="Add Red Flag"
            allEntities={allFlags}
            excludeIds={category.flag_refs || []}
            getLabel={(f) => f.name}
            getPreview={(f) => f.description || ''}
            onSelect={(id) => dispatch({ type: 'ADD_FLAG_TO_CATEGORY', categoryIndex, flagId: id })}
            allConfigs={allConfigs}
            getConfigRefs={getConfigFlagRefs}
          />
        </>
      )}
    </div>
  );
}

export default function RedFlagsSection({ config, allFlags, allConfigs, dispatch }) {
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const categories = config.red_flags || [];

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    dispatch({ type: 'ADD_CATEGORY', categoryName: newCategoryName.trim() });
    setNewCategoryName('');
    setShowAddCategory(false);
  };

  return (
    <div>
      <div className="wfe-section-header">
        <span className="wfe-section-label">Red Flag Categories</span>
        <button className="wfe-btn-add" onClick={() => setShowAddCategory(true)}>
          + Add Category
        </button>
      </div>

      {showAddCategory && (
        <div className="wfe-add-group-form">
          <input
            className="wfe-input"
            placeholder="Category name, e.g. Infection"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
            autoFocus
          />
          <button className="wfe-btn-sm primary" onClick={handleAddCategory}>Add</button>
          <button className="wfe-btn-sm ghost" onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}>Cancel</button>
        </div>
      )}

      {categories.length === 0 ? (
        <div className="wfe-placeholder">
          <p>No red flag categories yet. Add a category to start configuring flags.</p>
        </div>
      ) : (
        <DraggableList
          items={categories}
          keyExtractor={(c) => c.category_name}
          onReorder={(newCats) => dispatch({ type: 'REORDER_CATEGORIES', categories: newCats })}
          renderItem={(category, idx, { dragListeners }) => (
            <CategoryCard
              category={category}
              categoryIndex={idx}
              dispatch={dispatch}
              allFlags={allFlags}
              allConfigs={allConfigs}
              dragListeners={dragListeners}
            />
          )}
        />
      )}
    </div>
  );
}
